// The read-aloud button (24 Oct 2026): tap to hear the words, tap again to
// stop. It uses the browser's own speech (the Web Speech API), with an
// Australian English voice when the device has one and a little slower than
// normal. No recording, no network call and no new service. Where the
// browser cannot speak, the button is not shown at all.
//
// It stops when the words change (the child moves on to the next question)
// and when it leaves the screen.
import { useEffect, useRef, useState } from 'react';
import { canReadAloud } from './readAloud';

// A little slower than normal, for children aged 6 and up.
const RATE = 0.9;

// An Australian English voice, or none (the browser then picks one for the
// en-AU language it is asked for).
function australianVoice(
  voices: SpeechSynthesisVoice[],
): SpeechSynthesisVoice | null {
  return (
    voices.find(
      (voice) => voice.lang.replace('_', '-').toLowerCase() === 'en-au',
    ) ?? null
  );
}

type Props = {
  // What is said, already in spoken words (readAloud.ts).
  text: string;
};

export function ReadAloudButton({ text }: Props) {
  const [speaking, setSpeaking] = useState(false);
  // The words being said now, so a late "ended" from words already stopped
  // is ignored.
  const current = useRef<SpeechSynthesisUtterance | null>(null);
  const [supported] = useState(canReadAloud);

  useEffect(() => {
    return () => {
      if (current.current === null) return;
      current.current = null;
      if (canReadAloud()) window.speechSynthesis.cancel();
      setSpeaking(false);
    };
  }, [text]);

  if (!supported || text.trim() === '') return null;

  function stop() {
    current.current = null;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }

  function start() {
    const speech = window.speechSynthesis;
    speech.cancel();
    const words = new window.SpeechSynthesisUtterance(text);
    words.lang = 'en-AU';
    words.rate = RATE;
    const voice = australianVoice(speech.getVoices());
    if (voice) words.voice = voice;
    const finished = () => {
      if (current.current !== words) return;
      current.current = null;
      setSpeaking(false);
    };
    words.onend = finished;
    words.onerror = finished;
    current.current = words;
    setSpeaking(true);
    speech.speak(words);
  }

  return (
    <button
      type="button"
      className="dm-btn-secondary min-h-11 px-4 focus-visible:outline-3 focus-visible:outline-offset-2"
      onClick={speaking ? stop : start}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="size-5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" />
        {speaking ? (
          <path d="M16 9l5 6M21 9l-5 6" />
        ) : (
          <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />
        )}
      </svg>
      {speaking ? 'Stop reading' : 'Read to me'}
    </button>
  );
}
